// Clio Document Picker Types

export interface ClioDocument {
  id: number;
  filename: string; // Can be null from API but normalized in service to 'Untitled Document'
  integration: string | null;
  size: number; // File size in bytes (mapped from latest_document_version.size)
  content_type: string; // MIME type
  created_at?: string; // ISO 8601
  updated_at: string; // ISO 8601
  fully_uploaded?: boolean;
  source: 'document';
  latest_document_version?: {
    id: number;
    name: string;
    size: number;
    version_number: number;
    fully_uploaded: boolean;
  };
  parent?: {
    id: number;
    name: string;
    type: string; // "Folder" | "Matter"
  };
  matter?: {
    id: number;
    display_number: string;
    description: string;
  } | null;
  etag?: string;

  // Enhanced metadata (added by service layer)
  file_extension?: string;
  file_type?: 'word' | 'unknown';
  file_size_formatted?: string; // "2.5 MB"
  created_at_formatted?: string; // "2023-12-01 14:30"
  updated_at_formatted?: string;
  created_at_relative?: string; // "2 hours ago"
  updated_at_relative?: string;
  matter_display?: string; // "MAT-001 - Contract Review"
  folder_name?: string;
}

export interface ClioFolder {
  id: number;
  name: string;
  parent: {
    id: number;
    name: string;
    type: string;
  };
  matter: {
    id: number;
    display_number: string;
    description: string;
  } | null;
  created_at: string;
  updated_at: string;
  etag: string;
}

export interface ClioDocumentTemplate {
  id: number;
  filename: string;
  size: number;
  content_type: string;
  created_at?: string;
  updated_at?: string;
  source: 'template';
}

export interface ClioApiResponse<T> {
  data?: T[];
  documents?: ClioDocument[];
  folders?: ClioFolder[];
  document_templates?: ClioDocumentTemplate[];
  next_page_token?: string;
  meta: {
    records?: number;
  };
  query?: string; // For search responses
  error?: string;
  code?:
    | 'CLIO_UNAUTHORIZED'
    | 'CLIO_INVALID_REQUEST'
    | 'CLIO_API_ERROR'
    | 'MISSING_QUERY'
    | 'DOCUMENT_NOT_FOUND'
    | 'DOWNLOAD_NOT_AVAILABLE';
}

export interface ClioApiResponseMatterRootFolder {
  id: number;
  display_number: string;
  folder: ClioFolder;
}

export interface DocumentSearchParams {
  q?: string;
  page_token?: string;
  limit?: number;
  matter_id?: number;
  parent_id?: number;
  name?: string;
  created_since?: string;
  updated_since?: string;
  only_fully_uploaded?: boolean;
  content_types?: string[];
}

export interface FolderSearchParams {
  page_token?: string;
  limit?: number;
  matter_id?: number;
  parent_id?: number;
}

export interface DocumentTemplateSearchParams {
  page_token?: string;
  limit?: number;
  name?: string;
  created_since?: string;
  updated_since?: string;
  content_types?: string[];
}

export interface DocumentTemplateSearchQuery {
  query: string;
  page_token?: string;
  limit?: number;
  content_types?: string[];
}

export interface BreadcrumbItem {
  id: number;
  name: string;
  type: 'folder' | 'matter' | 'root';
}

export type ViewMode = 'list' | 'grid';
export type SortBy = 'name' | 'updated_at' | 'size' | 'created_at';
export type SortOrder = 'asc' | 'desc';

export interface DocumentPickerState {
  // Navigation
  currentFolderId: number | null;
  breadcrumbs: BreadcrumbItem[];

  // Data
  documents: ClioDocument[];
  folders: ClioFolder[];
  selectedDocument: ClioDocument | null;

  // UI State
  isLoading: boolean;
  viewMode: ViewMode;
  searchQuery: string;
  searchSuggestions: string[];

  // Pagination
  hasMore: boolean;
  nextPageToken: string | null;

  // Filters
  selectedMatter: number | null;
  sortBy: SortBy;
  sortOrder: SortOrder;
  fileTypeFilter: string[];

  // Error handling
  error: string | null;
  retryCount: number;
}

export interface DocumentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (items: SelectableItem[]) => Promise<void>;
  orgId: string;
  initialMatterId?: number;
  allowMultiSelect?: boolean;
  title?: string;
  maxFileSize?: number; // in bytes
  maxSelectionLimit?: number; // maximum number of items that can be selected
  allowedFileTypes?: string[];
}

export interface DocumentItemProps {
  document: ClioDocument;
  viewMode: ViewMode;
  isSelected: boolean;
  onSelect: (document: ClioDocument) => void;
  onDoubleClick?: (document: ClioDocument) => void;
}

export interface FolderItemProps {
  folder: ClioFolder;
  viewMode: ViewMode;
  onNavigate: (folderId: number) => void;
  onDoubleClick?: (folderId: number) => void;
}

export type DocumentPickerTab =
  (typeof DOCUMENT_PICKER_TABS)[keyof typeof DOCUMENT_PICKER_TABS];
export type DocumentPickerFilterMode =
  (typeof DOCUMENT_PICKER_FILTER_MODES)[keyof typeof DOCUMENT_PICKER_FILTER_MODES];

// Union type for selectable items
export type SelectableItem = ClioDocument | ClioDocumentTemplate;

// Item source constants (match the source field values)
export const ITEM_SOURCES = {
  DOCUMENT: 'document',
  TEMPLATE: 'template',
} as const;

// Type guard to check if an item is a document
export const isClioDocument = (item: SelectableItem): item is ClioDocument => {
  return item.source === ITEM_SOURCES.DOCUMENT;
};

// Type guard to check if an item is a template
export const isClioDocumentTemplate = (
  item: SelectableItem,
): item is ClioDocumentTemplate => {
  return item.source === ITEM_SOURCES.TEMPLATE;
};

export interface DocumentPickerHeaderProps {
  title: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClose: () => void;
  activeTab: DocumentPickerTab;
  onTabChange: (tab: DocumentPickerTab) => void;
}

export interface DocumentPickerNavigationProps {
  breadcrumbs: BreadcrumbItem[];
  onNavigate: (folderId: number | null) => void;
  onBack: () => void;
  canGoBack: boolean;
  showRecentDocuments: () => void;
  isRecentMode: boolean;
  isSearchMode: boolean;
}

export interface DocumentPickerContentProps {
  documents: ClioDocument[];
  folders: ClioFolder[];
  selectedDocuments: ClioDocument[];
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  onDocumentToggle: (document: ClioDocument) => void;
  onFolderNavigate: (folderId: number) => void;
  onLoadMore: () => void;
  onRetry: () => void;
  breadcrumbs: BreadcrumbItem[];
  filterMode: DocumentPickerFilterMode;
  onFilterChange: (mode: DocumentPickerFilterMode) => void;
}

export interface DocumentPickerFooterProps {
  selectedItems: SelectableItem[];
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isDownloading?: boolean;
}

// Document picker tab constants
export const DOCUMENT_PICKER_TABS = {
  MATTER_DOCUMENTS: 'matter-documents',
  DOCUMENT_TEMPLATES: 'document-templates',
} as const;

// Document picker filter mode constants
export const DOCUMENT_PICKER_FILTER_MODES = {
  ALL: 'all',
  FILES_ONLY: 'files-only',
} as const;

// File type icon mapping (DOCX only)
export const FILE_TYPE_ICONS = {
  word: '📝',
  unknown: '📄',
} as const;

// File type colors for styling (DOCX only)
export const FILE_TYPE_COLORS = {
  word: 'text-blue-600',
  unknown: 'text-gray-400',
  folder: 'text-yellow-600',
} as const;
